import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
    name: 'stackTraceHighlight',
    standalone: false
})
export class StackTraceHighlightPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(trace: string | null | undefined): SafeHtml {
    if (!trace) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    const escaped = trace
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const highlighted = escaped
      .split('\n')
      .map(line => this.highlightLine(line))
      .join('\n');

    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  private highlightLine(line: string): string {
    // "at" frame lines:  "   at Namespace.Class.Method(params)"
    const frameMatch = line.match(/^(\s*)(at\s+)(.*?)(\(.*\))(.*)$/);
    if (frameMatch) {
      const [, indent, atKeyword, qualifiedName, params, rest] = frameMatch;
      const nameHighlighted = this.highlightQualifiedName(qualifiedName);
      return indent
        + `<span class="stack-at">${atKeyword}</span>`
        + nameHighlighted
        + `<span class="stack-params">${params}</span>`
        + rest;
    }

    // "in" file location:  "   in C:\path\file.cs:line 42"
    const fileMatch = line.match(/^(\s*)(in\s+)(.+?)(:line\s+\d+)(.*)$/);
    if (fileMatch) {
      const [, indent, inKeyword, filePath, lineRef, rest] = fileMatch;
      return indent
        + `<span class="stack-at">${inKeyword}</span>`
        + `<span class="stack-file">${filePath}</span>`
        + `<span class="stack-line-ref">${lineRef}</span>`
        + rest;
    }

    // Exception header: "ExceptionType: message"
    const exMatch = line.match(/^(\s*)([\w.]+(?:Exception|Error))(:?\s?)(.*)$/);
    if (exMatch) {
      const [, indent, exType, separator, message] = exMatch;
      return indent
        + `<span class="stack-exception">${exType}</span>`
        + separator
        + `<span class="stack-message">${message}</span>`;
    }

    // "--- End of inner exception stack trace ---" dividers
    if (/---/.test(line)) {
      return `<span class="stack-divider">${line}</span>`;
    }

    return line;
  }

  private highlightQualifiedName(name: string): string {
    const lastDot = name.lastIndexOf('.');
    if (lastDot === -1) {
      return `<span class="stack-method">${name}</span>`;
    }
    const namespace = name.substring(0, lastDot + 1);
    const method = name.substring(lastDot + 1);
    return `<span class="stack-namespace">${namespace}</span><span class="stack-method">${method}</span>`;
  }
}
